import { LinearGradient } from 'expo-linear-gradient';
import type { ReactNode } from 'react';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

const BANNER_GRADIENT = colors.surface.bento.gradient;
const GLOW_GRADIENT = colors.gradients.activeWash;

interface DashboardHeaderProps {
  badge?: string;
  title: string;
  titleTestID?: string;
  subtitle?: string;
  icon?: ReactNode;
  renderActions?: () => ReactNode;
}

export function DashboardHeader({
  badge,
  title,
  titleTestID,
  subtitle,
  icon,
  renderActions,
}: Readonly<DashboardHeaderProps>) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const isTablet = width >= tokens.breakpoints.tablet;

  return (
    <FadeIn delay={0} translateY={14}>
      <Box
        borderWidth={1}
        borderColor={colors.primary.borderActive}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: tokens.radius.xl,
          marginBottom: tokens.spacing.xl,
          paddingHorizontal: isTablet ? tokens.spacing['2xl'] : tokens.spacing.xl,
          paddingVertical: isDesktop ? tokens.spacing['2xl'] : tokens.spacing.xl,
        }}
      >
        <LinearGradient
          colors={BANNER_GRADIENT}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0.5 }}
          style={StyleSheet.absoluteFill}
        />
        <LinearGradient
          colors={GLOW_GRADIENT}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          style={{
            position: 'absolute',
            top: -80,
            right: -40,
            width: 320,
            height: 220,
            borderRadius: 160,
            opacity: 0.9,
          }}
          pointerEvents="none"
        />
        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap="md"
          style={{ position: 'relative' }}
        >
          <VStack spacing="md" style={{ flex: 1 }}>
            {badge && (
              <Box
                backgroundColor={colors.primary.muted}
                paddingX="md"
                paddingY="xs"
                borderRadius="full"
                borderWidth={1}
                borderColor={colors.primary.borderActive}
                alignSelf="flex-start"
              >
                <Text
                  variant="caption"
                  color={colors.primary[300]}
                  style={{
                    fontWeight: '700',
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    fontSize: Platform.select({ web: 11, default: 10 }),
                  }}
                >
                  {badge}
                </Text>
              </Box>
            )}

            <Text
              testID={titleTestID}
              variant={isDesktop ? 'h1' : 'h2'}
              color={colors.text.primary}
              style={{
                fontWeight: '700',
                letterSpacing: -1,
                // Sora's ExtraBold ascenders are taller than the previous
                // display font, so a ~1.0x line box clipped the glyph tops on
                // iOS. Give the display family ~1.2x headroom.
                lineHeight: isDesktop ? 60 : 44,
                maxWidth: isDesktop ? 760 : undefined,
              }}
            >
              {title}
            </Text>

            {subtitle && (
              <Text
                variant="body"
                color={colors.text.secondary}
                style={{
                  maxWidth: 620,
                  lineHeight: Platform.select({ web: 24, default: 22 }),
                }}
              >
                {subtitle}
              </Text>
            )}
          </VStack>

          {icon && <Box style={{ alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>}

          {renderActions && (
            <HStack spacing="sm" align="center">
              {renderActions()}
            </HStack>
          )}
        </Box>
      </Box>
    </FadeIn>
  );
}
